import React, { useEffect, useState } from "react";
import {
  InputField, SelectField, Label
} from "../../components/ui/UIComponents";
import {
  getStates, getDistricts, getColleges,
  getDistrictInfo, calcWorkProbability, getProximityLabel
} from "../../data/indiaData";

// ─────────────────────────────────────────────────────────────────────────────
// STEP 1 — Basic Details + Location Cascade (State → District → College)
// ─────────────────────────────────────────────────────────────────────────────

export default function Step1Basic({ form, setForm }) {
  const [workScore, setWorkScore]     = useState(null);
  const [districtInfo, setDistrictInfo] = useState(null);
  const [colData, setColData]         = useState(null);

  const states    = getStates();
  const districts = getDistricts(form.state);
  const colleges  = getColleges(form.state, form.district);

  const update = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const onStateChange = val => {
    setForm(f => ({ ...f, state: val, district: "", college: "", nearestMetro: "" }));
    setWorkScore(null); setDistrictInfo(null); setColData(null);
  };

  const onDistrictChange = val => {
    const info = getDistrictInfo(form.state, val);
    setDistrictInfo(info);
    setForm(f => ({ ...f, district: val, college: "", nearestMetro: info?.nearestMetro || "" }));
    setWorkScore(null); setColData(null);
  };

  const onCollegeChange = val => {
    update("college", val);
    const c = colleges.find(x => x.name === val);
    setColData(c || null);
    if (form.state && form.district) {
      setWorkScore(calcWorkProbability(form.state, form.district, val));
    }
  };

  const scoreColor =
    workScore >= 80 ? "#10b981" :
    workScore >= 60 ? "#22d3ee" :
    workScore >= 40 ? "#f59e0b" : "#ef4444";

  const scoreLabel =
    workScore >= 80 ? "Excellent Opportunity" :
    workScore >= 60 ? "Good Opportunity" :
    workScore >= 40 ? "Moderate Opportunity" : "Limited — Consider Relocation";

  return (
    <div>
      {/* ── Personal ── */}
      <InputField
        label="Full Name" required
        value={form.fullName}
        onChange={v => update("fullName", v)}
        placeholder="e.g. Ravi Kumar"
      />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <InputField
          label="Age" type="number" required
          value={form.age}
          onChange={v => update("age", v)}
          placeholder="21"
        />
        <SelectField
          label="Gender" required
          value={form.gender}
          onChange={v => update("gender", v)}
          options={["Male","Female","Non-binary","Prefer not to say"]}
        />
      </div>

      {/* ── Location Cascade Box ── */}
      <div style={{
        background: "rgba(34,211,238,0.04)",
        border: "1px solid rgba(34,211,238,0.14)",
        borderRadius: 12, padding: 18, marginBottom: 16,
      }}>
        <p style={{
          fontFamily: "Rajdhani, sans-serif", fontSize: 12,
          color: "#22d3ee", letterSpacing: 2.5, marginBottom: 16,
        }}>
          📍 LOCATION &amp; INSTITUTION
        </p>

        {/* State */}
        <SelectField
          label="State" required
          value={form.state}
          onChange={onStateChange}
          options={states}
          hint="Choose your state to unlock districts"
        />

        {/* District — unlocked after state */}
        <SelectField
          label="District" required
          value={form.district}
          onChange={onDistrictChange}
          options={districts}
          disabled={!form.state}
          hint={!form.state ? "⬆ Select state first" : `${districts.length} districts available`}
        />

        {/* College — unlocked after district */}
        <SelectField
          label="College / Institution" required
          value={form.college}
          onChange={onCollegeChange}
          options={colleges.map(c => ({
            value: c.name,
            label: `${c.name} — ${c.placement}% placed`,
          }))}
          disabled={!form.district}
          hint={!form.district ? "⬆ Select district first" : `${colleges.length} colleges in this district`}
        />

        {/* Area Type */}
        <SelectField
          label="Area Type"
          value={form.areaType}
          onChange={v => update("areaType", v)}
          options={["Rural","Semi-Urban","Urban","Metro"]}
        />

        {/* ── Metro Proximity Card — shown when district selected ── */}
        {districtInfo && (
          <div style={{
            marginTop: 4, padding: "12px 16px",
            background: "rgba(6,13,26,0.8)",
            border: "1px solid rgba(34,211,238,0.18)",
            borderRadius: 10,
            display: "flex", flexDirection: "column", gap: 8,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
              <span style={{ fontSize: 14, color: "#94b8d4", fontFamily: "Exo 2, sans-serif" }}>
                {getProximityLabel(form.state, form.district)}
              </span>
              <span style={{
                fontSize: 11, fontFamily: "Rajdhani, sans-serif",
                background: "rgba(34,211,238,0.1)", border: "1px solid rgba(34,211,238,0.3)",
                color: "#22d3ee", padding: "2px 10px", borderRadius: 12, letterSpacing: 1,
              }}>
                Metro Score: {districtInfo.workProximityScore}%
              </span>
            </div>

            {/* Metro distance bar */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: "#6b8aad", fontFamily: "Exo 2" }}>Metro Proximity</span>
                <span style={{ fontSize: 11, color: "#22d3ee", fontFamily: "Rajdhani" }}>{districtInfo.workProximityScore}%</span>
              </div>
              <div style={{ height: 3, background: "rgba(34,211,238,0.08)", borderRadius: 4 }}>
                <div style={{
                  height: "100%", borderRadius: 4,
                  width: `${districtInfo.workProximityScore}%`,
                  background: "linear-gradient(90deg,#22d3ee,#3b82f6)",
                  transition: "width 0.8s ease",
                }} />
              </div>
            </div>
          </div>
        )}

        {/* ── College Info Card — shown when college selected ── */}
        {colData && (
          <div style={{
            marginTop: 10, padding: "14px 16px",
            background: "linear-gradient(135deg,rgba(34,211,238,0.05),rgba(59,130,246,0.05))",
            border: "1px solid rgba(34,211,238,0.2)",
            borderRadius: 10,
          }}>
            <p style={{ fontFamily: "Rajdhani", fontSize: 11, color: "#22d3ee", letterSpacing: 2, marginBottom: 10 }}>
              🏛️ COLLEGE STATS
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
              {[
                { label: "Placement Rate", value: `${colData.placement}%`, color: colData.placement >= 85 ? "#10b981" : colData.placement >= 70 ? "#22d3ee" : "#f59e0b" },
                { label: "Avg Package",   value: `₹${colData.avgPackage} LPA`, color: "#a855f7" },
                { label: "Type",          value: colData.type, color: "#3b82f6" },
              ].map(item => (
                <div key={item.label} style={{ textAlign: "center" }}>
                  <div style={{ fontFamily: "Rajdhani", fontSize: 18, fontWeight: 700, color: item.color }}>{item.value}</div>
                  <div style={{ fontSize: 10, color: "#6b8aad", fontFamily: "Exo 2", marginTop: 2 }}>{item.label}</div>
                </div>
              ))}
            </div>

            {/* Placement bar */}
            <div style={{ marginTop: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: "#6b8aad", fontFamily: "Exo 2" }}>Campus Placement</span>
                <span style={{ fontSize: 11, color: "#10b981", fontFamily: "Rajdhani" }}>{colData.placement}%</span>
              </div>
              <div style={{ height: 4, background: "rgba(16,185,129,0.1)", borderRadius: 4 }}>
                <div style={{
                  height: "100%", borderRadius: 4,
                  width: `${colData.placement}%`,
                  background: "linear-gradient(90deg,#10b981,#22d3ee)",
                  transition: "width 0.8s ease",
                }} />
              </div>
            </div>
          </div>
        )}

        {/* ── WORK PROBABILITY SCORE — shown when all 3 selected ── */}
        {workScore !== null && (
          <div style={{
            marginTop: 14, padding: "18px 20px",
            background: `linear-gradient(135deg,${scoreColor}0d,rgba(6,13,26,0.9))`,
            border: `1px solid ${scoreColor}40`,
            borderRadius: 12,
            animation: "slide-in-up 0.4s ease forwards",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
              <div>
                <p style={{ fontFamily: "Rajdhani", fontSize: 11, color: scoreColor, letterSpacing: 2.5, marginBottom: 4 }}>
                  🎯 WORK PROBABILITY SCORE
                </p>
                <p style={{ fontSize: 12, color: "#94b8d4", fontFamily: "Exo 2" }}>
                  Based on college placement × metro proximity
                </p>
              </div>
              <div style={{
                fontFamily: "Rajdhani", fontSize: 48, fontWeight: 700, color: scoreColor,
                lineHeight: 1, textShadow: `0 0 20px ${scoreColor}66`,
              }}>
                {workScore}<span style={{ fontSize: 22 }}>%</span>
              </div>
            </div>

            {/* Score bar */}
            <div style={{ marginTop: 14 }}>
              <div style={{ height: 6, background: "rgba(255,255,255,0.05)", borderRadius: 6, overflow: "hidden" }}>
                <div style={{
                  height: "100%", borderRadius: 6,
                  width: `${workScore}%`,
                  background: `linear-gradient(90deg,${scoreColor},${scoreColor}88)`,
                  boxShadow: `0 0 10px ${scoreColor}55`,
                  transition: "width 1s ease",
                }} />
              </div>
            </div>

            <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: scoreColor, flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: scoreColor, fontFamily: "Rajdhani", fontWeight: 600, letterSpacing: 0.5 }}>
                {scoreLabel}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}